<template>
    <div class="batch-actions">
      <el-card shadow="never">
        <el-form :inline="true" :model="form" class="batch-form">
          <el-form-item label="批量操作">
            <el-select v-model="form.action" placeholder="选择操作">
              <el-option label="更改状态" value="status"></el-option>
              <el-option label="删除问题单" value="delete"></el-option>
            </el-select>
          </el-form-item>
          
          <el-form-item v-if="form.action === 'status'">
            <el-select v-model="form.status_id" placeholder="选择状态">
              <el-option
                v-for="status in statuses"
                :key="status.id"
                :label="status.name"
                :value="status.id">
              </el-option>
            </el-select>
          </el-form-item>
          
          <el-form-item v-if="form.action === 'status'">
            <el-input
              v-model="form.notes"
              placeholder="备注"
              clearable>
            </el-input>
          </el-form-item>

          <template v-if="form.action === 'status' && [5, 8].includes(Number(form.status_id))">
            <el-form-item>
              <el-select
                v-model="form.fixed_version"
                placeholder="选择修复版本"
                style="width: 300px">
                <el-option
                  v-for="version in versions"
                  :key="version.id"
                  :label="version.name"
                  :value="version.id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-select
                v-model="form.solution"
                placeholder="选择硬件修复版本"
                style="width: 300px">
                <el-option
                  v-for="version in projectVersions"
                  :key="version.id"
                  :label="version.name"
                  :value="version.id">
                </el-option>
              </el-select>
            </el-form-item>
          </template>
          
          <el-form-item>
            <el-button 
              type="primary" 
              @click="handleBatchAction"
              :disabled="!selectedIssues.length || (form.action === 'status' && !form.status_id)"
            >
              执行
            </el-button>
          </el-form-item>
          
          <el-form-item>
            <el-button @click="$emit('refresh')">
              刷新
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </template>
  
  <script>
  export default {
    name: 'BatchActions',
    props: {
      selectedIssues: {
        type: Array,
        default: () => []
      },
      projectId: {
        type: Number,
        default: null
      }
    },
    data() {
      return {
        form: {
          action: '',
          status_id: '',
          notes: '',
          fixed_version: '',
          solution: ''
        },
        statuses: [],
        projectVersions: [],
        versions: [],
        solutionFieldId: null
      }
    },
    created() {
      this.fetchStatuses()
    },
    watch: {
      projectId: {
        handler(newVal) {
          if (newVal) {
            const projectId = Number(newVal)
            if (!isNaN(projectId)) {
              this.fetchProjectVersions(projectId)
            }
          } else {
            this.projectVersions = []
          }
        },
        immediate: true
      }
    },
    methods: {
      async fetchStatuses() {
        try {
          const response = await this.$axios.get('/api/statuses')
          if (response.data.success) {
            this.statuses = response.data.data.issue_statuses || []
          }
        } catch (error) {
          this.$message.error('获取状态列表失败: ' + error.message)
        }
      },
      async handleBatchAction() {
        if (!this.selectedIssues.length) {
          this.$message.warning('请至少选择一个问题单')
          return
        }
        
        // Validate fixed version is selected when required
        if (this.form.action === 'status' && 
            [5, 8].includes(Number(this.form.status_id)) && 
            !this.form.fixed_version) {
          this.$message.warning('请选择修复版本')
          return
        }

        try {
          let response
          if (this.form.action === 'status') {
            let notes = this.form.notes
            // Call new API when project is selected and status is resolved/closed
            if (this.projectId && [5, 8].includes(Number(this.form.status_id))) {
              const requestData = {
                issue_ids: this.selectedIssues,
                project_id: this.projectId,
                status_id: this.form.status_id,
                notes: this.form.notes,
                version_id: this.form.fixed_version,  // Changed from fixed_version to version_id
                solution: {
                  field_id: this.solutionFieldId,
                  value: this.form.solution || ''
                }
              }
              response = await this.$axios.put('/api/issues/batch_update', requestData, {
                headers: {
                  'Content-Type': 'application/json'
                }
              })
            } else {
              // Original update for other cases
              let notes = this.form.notes
              if ([5, 8].includes(Number(this.form.status_id))) {
                const additionalNotes = [
                  this.form.solution && `解决方案: ${this.form.solution}`,
                  this.form.fixed_version && `修复版本: ${this.form.fixed_version}`
                ].filter(Boolean).join('\n')
                notes = [notes, additionalNotes].filter(Boolean).join('\n\n')
                if (this.form.solution) {
                  this.form.solution = this.form.solution.trim()
                }
              }

              const requestData = {
                issue_ids: this.selectedIssues,
                status_id: this.form.status_id,
                notes: notes
              }
              response = await this.$axios.put('/api/issues/batch_update', requestData, {
                headers: {
                  'Content-Type': 'application/json'
                }
              })
            }
          } else if (this.form.action === 'delete') {
            try {
              await this.$confirm(`确定要删除选中的 ${this.selectedIssues.length} 个问题单吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
              })
              const data = { issue_ids: this.selectedIssues }
              response = await this.$axios.delete('/api/issues/batch_delete', { data })
            } catch (cancel) {
              return // User cancelled the deletion
            }
          }
          
          if (response.data.success) {
            this.$message.success('批量操作成功')
            this.$emit('refresh')
            this.resetForm()
          } else {
            this.$message.error('操作失败: ' + (response.data.error || '未知错误'))
          }
        } catch (error) {
          this.$message.error('操作失败: ' + error.message)
        }
      },
      async fetchProjectVersions(projectId) {
        try {
          // Get fixed versions from new API
          const versionsResponse = await this.$axios.get(`/api/projects/${projectId}/fixed_versions`)
          if (versionsResponse.data.success) {
            this.versions = versionsResponse.data.data.versions || []
          }

          // Keep original custom fields logic
          const response = await this.$axios.get(`/api/projects/${projectId}/custom_fields`)
          this.projectVersions = []
          if (response.data.success) {
            const fixedVersionField = response.data.data.find(field => 
              field.name.toLowerCase().includes('fixed version') ||
              field.name.toLowerCase().includes('修复')
            )
            if (fixedVersionField && fixedVersionField.possible_values) {
               this.solutionFieldId = fixedVersionField.id
               this.projectVersions = fixedVersionField.possible_values.map(item => ({
                 id: item.value || item.id || item,
                 name: item.label || item.name || item
               }))
            }
          }
        } catch (error) {
          this.$message.error('获取版本数据失败: ' + error.message)
        }
      },
      resetForm() {
        this.form = {
          action: '',
          status_id: '',
          notes: '',
          fixed_versions: [],
          solution: ''
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .batch-actions {
    margin-bottom: 20px;
  }
  .batch-form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
  </style>



// WEBPACK FOOTER //
// src/components/BatchActions.vue